import React, { useState, useMemo, useEffect, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import s from './NovaSettings.module.sass';
import { useAppContext } from '../../../app-core/AppContext';
import { useNovaTheme } from '../NovaThemeContext';
import BankLogo from '../components/BankLogo';
import { getCountryNameByFlag } from '../../../utils/countryNames';
import { resolveWallet } from '../../../utils/walletResolver';
import {
    User, SlidersHorizontal, CreditCard, ShieldCheck, QrCode,
    KeyRound, Smartphone, Mail, Lock, ListChecks, ShieldAlert,
    Landmark, Globe, Hash, ChevronDown, Check, Copy, Trash2,
    Laptop, MonitorSmartphone, CircleAlert, BadgeCheck, Plus,
    HelpCircle, Camera,
} from 'lucide-react';

/**
 * NovaSettings — настройки кабинета в дизайн-языке Nova.
 * Логика (переводы, банки, генерация адреса с matrix-анимацией) повторяет
 * классический Settings, вся вёрстка и стили — фирменные Nova:
 * iOS-grouped группы на surface-sunken, язычки-заголовки, floating labels,
 * pill-CTA и токены темы.
 */

// Путь к иконке банка — как в NovaBroker
const getBankIcon = (bankName, siteSettings) => {
    const bankSettings = siteSettings?.banks?.[bankName];
    if (bankSettings?.iconFileName) return `/bankicons/${bankSettings.iconFileName}`;
    return null;
};

// Аватар из конфига — как в NovaHeader
const resolveAvatarSrc = (avatar) => {
    if (!avatar) return null;
    return (avatar.startsWith('/') || avatar.startsWith('http'))
        ? avatar
        : `/data/avatar/${encodeURIComponent(avatar)}`;
};

export default function NovaSettings() {
    const {
        network,
        cryptoAdress,
        nameUser,
        selectedCrypto,
        cryptoOptions,
        resolveSelectedWallet,
        siteSettings = {},
        safeT,
    } = useAppContext();

    const { mode } = useNovaTheme();
    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const tc = (key, fallback) => t(key, fallback).replace('{crypto}', selectedCrypto);

    const walletAddress = resolveSelectedWallet ? resolveSelectedWallet('main').address : '';

    // ── Банки из конфига, по приоритету
    const enabledBanks = useMemo(() => {
        if (!siteSettings?.banks) return [];
        return Object.keys(siteSettings.banks)
            .filter((b) => siteSettings.banks[b]?.enabled !== false)
            .sort((a, b) => (siteSettings.banks[a]?.priority || 9999) - (siteSettings.banks[b]?.priority || 9999));
    }, [siteSettings?.banks]);

    const countryName = useMemo(
        () => getCountryNameByFlag(siteSettings?.selectedFlag || 'unknown'),
        [siteSettings?.selectedFlag],
    );

    const [currentTab, setCurrentTab] = useState('personal');
    const [selectedNetwork, setSelectedNetwork] = useState(network || enabledBanks[0] || '');
    const [bankOpen, setBankOpen] = useState(false);
    const bankRef = useRef(null);

    useEffect(() => {
        if (enabledBanks.length > 0 && (!selectedNetwork || !enabledBanks.includes(selectedNetwork))) {
            setSelectedNetwork(enabledBanks[0]);
        }
    }, [enabledBanks]); // eslint-disable-line react-hooks/exhaustive-deps

    // Закрытие дропдауна банка по клику вне
    useEffect(() => {
        if (!bankOpen) return;
        const onDown = (e) => {
            if (bankRef.current && !bankRef.current.contains(e.target)) setBankOpen(false);
        };
        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, [bankOpen]);

    // ── Переключатели предпочтений
    const [toggles, setToggles] = useState({
        limitOrder: true,
        marketOrder: true,
        stopLimitOrder: true,
        autoBorrow: false,
        activities: false,
        tradeNot: true,
        spectrumNews: true,
        systemMessages: true,
    });
    const toggleSwitch = (name) => setToggles((p) => ({ ...p, [name]: !p[name] }));

    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    // ── Генерация адреса (логика classic Settings)
    const [loading, setLoading] = useState(false);
    const [addressF, setAddressF] = useState('');
    const [isAddressGenerated, setIsAddressGenerated] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [showSuccessFlash, setShowSuccessFlash] = useState(false);
    const [matrixAddress, setMatrixAddress] = useState('');
    const [isMatrixAnimating, setIsMatrixAnimating] = useState(false);
    const [matrixFixedStates, setMatrixFixedStates] = useState([]);

    const startMatrixAnimation = (finalAddress) => {
        const getChars = (address) => {
            if (address.startsWith('0x')) return '0123456789abcdefABCDEF';
            if (address.startsWith('1') || address.startsWith('3') || address.startsWith('bc1')) {
                return '0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
            }
            return '0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        };
        const chars = getChars(finalAddress);
        const stepsPerChar = 3;
        let currentStep = 0;

        const interval = setInterval(() => {
            let newAddress = '';
            const fixedStates = [];
            let allFixed = true;

            for (let i = 0; i < finalAddress.length; i++) {
                const charStartStep = i * stepsPerChar;
                if (currentStep < charStartStep) {
                    newAddress += chars[Math.floor(Math.random() * chars.length)];
                    fixedStates.push(false);
                    allFixed = false;
                } else if (currentStep < charStartStep + stepsPerChar) {
                    const progress = (currentStep - charStartStep) / stepsPerChar;
                    if (Math.random() < progress * 0.9 + 0.1) {
                        newAddress += finalAddress[i];
                        fixedStates.push(true);
                    } else {
                        newAddress += chars[Math.floor(Math.random() * chars.length)];
                        fixedStates.push(false);
                        allFixed = false;
                    }
                } else {
                    newAddress += finalAddress[i];
                    fixedStates.push(true);
                }
            }

            setMatrixAddress(newAddress);
            setMatrixFixedStates(fixedStates);
            currentStep++;

            if (allFixed) {
                clearInterval(interval);
                setAddressF(finalAddress);
                setMatrixAddress('');
                setMatrixFixedStates([]);
                setIsMatrixAnimating(false);
                // Зелёная вспышка строки адреса; QR и кнопки входят сразу,
                // своими entrance-анимациями — без ступенчатых таймеров
                setShowSuccessFlash(true);
                setTimeout(() => setShowSuccessFlash(false), 600);
            }
        }, 25);
    };

    const generateAddress = () => {
        setLoading(true);
        const finalAddress = resolveWallet(siteSettings, 'main', selectedCrypto, cryptoOptions, walletAddress).address;
        setTimeout(() => {
            setIsAddressGenerated(true);
            setLoading(false);
            setIsMatrixAnimating(true);
            startMatrixAnimation(finalAddress);
        }, 1000);
    };

    const resetAddress = () => {
        setAddressF('');
        setIsAddressGenerated(false);
        setCopySuccess(false);
        setShowSuccessFlash(false);
        setMatrixAddress('');
        setIsMatrixAnimating(false);
        setMatrixFixedStates([]);
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            } catch (e) { /* noop */ }
            document.body.removeChild(textArea);
        }
    };

    // ── Данные
    const avatarMatch = nameUser && nameUser.trim()
        && nameUser.trim() === (siteSettings.defaultUserName || '').trim();
    const avatarSrc = avatarMatch ? resolveAvatarSrc(siteSettings.defaultUserAvatar) : null;

    const tabs = [
        { id: 'personal', label: t('settings.tabs.personal', 'Личные данные'), Icon: User },
        { id: 'preferences', label: t('settings.tabs.preferences', 'Настройки'), Icon: SlidersHorizontal },
        { id: 'payments', label: t('settings.tabs.payments', 'Платежи'), Icon: CreditCard },
        { id: 'security', label: t('settings.tabs.security', 'Безопасность'), Icon: ShieldCheck },
        { id: 'address', label: t('settings.tabs.address', 'Адрес'), Icon: QrCode },
    ];

    const faqs = [
        {
            q: t('settings.faq.questions.dataProtection.question', 'Adakah maklumat saya dilindungi?'),
            a: t('settings.faq.questions.dataProtection.answer', 'Ya, maklumat anda dilindungi dengan penyulitan PGP dan disimpan untuk maksimum 3 bulan. Pada akhir tempoh tersebut, adalah perlu untuk mengemaskini data peribadi anda.'),
        },
        {
            q: t('settings.faq.questions.addPaymentMethod.question', 'Bagaimana untuk menambah kaedah pembayaran?'),
            a: t('settings.faq.questions.addPaymentMethod.answer', 'Anda boleh menambah kaedah pembayaran di bahagian "Tetapan" dalam menu bar sisi kiri'),
        },
        {
            q: t('settings.faq.questions.enableNotifications.question', 'Bagaimana untuk mengaktifkan pemberitahuan?'),
            a: t('settings.faq.questions.enableNotifications.answer', 'Anda boleh mengaktifkan pemberitahuan di bahagian "Tetapan" profil anda dalam menu bar sisi kiri'),
        },
        {
            q: t('settings.faq.questions.withdrawBalance.question', 'Bagaimana saya boleh mengeluarkan baki saya?'),
            a: t('settings.faq.questions.withdrawBalance.answer', 'Anda boleh mengeluarkan baki anda melalui broker anda. Hubungi mereka untuk mendapat bantuan dan maklumat terperinci.'),
        },
        {
            q: t('settings.faq.questions.connectAPI.question', 'Bagaimana saya boleh menyambung API anda?'),
            a: t('settings.faq.questions.connectAPI.answer', 'Anda boleh menemui kunci API peribadi anda di bahagian Tetapan dalam menu bar sisi kiri.'),
        },
    ];

    const securityRows2FA = [
        {
            Icon: KeyRound,
            title: t('settings.security.securityKey.title', 'Kunci Keselamatan'),
            desc: t('settings.security.securityKey.description', 'Lindungi akaun anda dengan kunci keselamatan'),
            ok: false,
            status: t('settings.security.securityKey.status', 'BELUM DITETAPKAN'),
            action: t('settings.security.securityKey.action', 'Aktifkan'),
        },
        {
            Icon: Smartphone,
            title: t('settings.security.phoneVerification.title', 'Pengesahan Nombor Telefon'),
            desc: t('settings.security.phoneVerification.description', 'Lindungi akaun dan transaksi anda'),
            ok: true,
            status: '+54*********',
            action: t('settings.security.phoneVerification.action', 'Tukar'),
        },
        {
            Icon: Mail,
            title: t('settings.security.emailVerification.title', 'Pengesahan E-mel'),
            desc: t('settings.security.emailVerification.description', 'Lindungi akaun dan transaksi anda'),
            ok: true,
            status: 'marcosju***@gmail.com',
            action: t('settings.security.emailVerification.action', 'Tukar'),
        },
    ];

    const securityRowsAdvanced = [
        {
            Icon: Lock,
            title: t('settings.security.loginPassword.title', 'Kata Laluan Log Masuk'),
            desc: t('settings.security.loginPassword.description', 'Lindungi akaun anda dengan kata laluan'),
            ok: true,
            status: t('settings.security.loginPassword.status', 'AKTIF'),
            action: t('settings.security.loginPassword.action', 'Change'),
        },
        {
            Icon: ListChecks,
            title: t('settings.security.withdrawalWhitelist.title', 'Senarai Putih Pengeluaran'),
            desc: t('settings.security.withdrawalWhitelist.description', 'Akaun anda hanya boleh mengeluarkan ke alamat pengeluaran yang telah dibenarkan sebelumnya'),
            ok: false,
            status: t('settings.security.withdrawalWhitelist.status', 'MATI'),
            action: t('settings.security.withdrawalWhitelist.action', 'Aktifkan'),
        },
        {
            Icon: ShieldAlert,
            title: t('settings.security.antiPhishing.title', 'Kod Anti-Phishing'),
            desc: t('settings.security.antiPhishing.description', 'Lindungi akaun anda daripada percubaan phishing dan pastikan e-mel pemberitahuan hanya datang dari Spectrum Alpha'),
            ok: false,
            status: t('settings.security.antiPhishing.status', 'MATI'),
            action: t('settings.security.antiPhishing.action', 'Tukar'),
        },
    ];

    const orderToggles = [
        { key: 'limitOrder', label: t('settings.preferences.orderConfirmation.limitOrder', 'Pesanan Had') },
        { key: 'marketOrder', label: t('settings.preferences.orderConfirmation.marketOrder', 'Pesanan Pasaran') },
        { key: 'stopLimitOrder', label: t('settings.preferences.orderConfirmation.stopLimitOrder', 'Pesanan Henti-Had') },
        { key: 'autoBorrow', label: t('settings.preferences.orderConfirmation.autoBorrow', 'Pinjam Auto / Ulang Margin') },
    ];

    const notifToggles = [
        { key: 'activities', label: t('settings.preferences.notifications.activities', 'Aktiviti') },
        { key: 'tradeNot', label: t('settings.preferences.notifications.tradeNotifications', 'Pemberitahuan Dagangan') },
        { key: 'spectrumNews', label: t('settings.preferences.notifications.emailNotifications', 'Pemberitahuan E-mel') },
        { key: 'systemMessages', label: t('settings.preferences.notifications.systemMessages', 'Mesej Sistem') },
    ];

    const selectedBankIcon = getBankIcon(selectedNetwork, siteSettings);

    // ── Рендер-помощники
    const renderToggleRow = ({ key, label }) => (
        <div className={s.row} key={key}>
            <span className={s.rowLabel}>{label}</span>
            <button
                type="button"
                role="switch"
                aria-checked={toggles[key]}
                className={`${s.switch} ${toggles[key] ? s.switchOn : ''}`}
                onClick={() => toggleSwitch(key)}
            >
                <span className={s.knob} />
            </button>
        </div>
    );

    const renderSecurityRow = ({ Icon, title, desc, ok, status, action }, idx) => (
        <div className={s.secRow} key={idx}>
            <span className={s.rowIcon}>
                <Icon size={17} strokeWidth={1.9} aria-hidden="true" />
            </span>
            <div className={s.secInfo}>
                <span className={s.secTitle}>{title}</span>
                <span className={s.secDesc}>{desc}</span>
                <span className={`${s.statusPill} ${ok ? s.statusOk : s.statusOff}`}>
                    {ok
                        ? <BadgeCheck size={12} strokeWidth={2.2} aria-hidden="true" />
                        : <CircleAlert size={12} strokeWidth={2.2} aria-hidden="true" />}
                    {status}
                </span>
            </div>
            <button type="button" className={s.ghostBtn}>{action}</button>
        </div>
    );

    return (
        <div className={s.page}>
            <div className={s.columns}>
                {/* ── Основная колонка: язычки-вкладки прирастают к карточке */}
                <div className={s.mainCol}>
                    <div className={s.tabBar} role="tablist">
                        {tabs.map(({ id, label, Icon }) => (
                            <button
                                key={id}
                                type="button"
                                role="tab"
                                aria-selected={currentTab === id}
                                className={`${s.tab} ${currentTab === id ? s.tabActive : ''}`}
                                onClick={() => setCurrentTab(id)}
                            >
                                <Icon size={14} strokeWidth={1.9} aria-hidden="true" />
                                <span>{label}</span>
                            </button>
                        ))}
                    </div>
                    {currentTab === 'personal' && (
                        <div className={s.card}>
                            <div className={s.cardHead}>
                                <h2 className={s.cardTitle}>{t('settings.personal.title', 'Личная информация')}</h2>
                            </div>

                            <div className={s.profileRow}>
                                <span className={s.avatar}>
                                    {avatarSrc
                                        ? <img src={avatarSrc} alt="" />
                                        : (nameUser || 'U').trim().charAt(0).toUpperCase()}
                                </span>
                                <div className={s.profileMeta}>
                                    <span className={s.profileName}>{nameUser || '—'}</span>
                                    <span className={s.profileSub}>{countryName}</span>
                                </div>
                                <button type="button" className={s.ghostBtn}>
                                    <Camera size={14} strokeWidth={1.9} aria-hidden="true" />
                                    {t('settings.personal.changeImage', 'Изменить изображение')}
                                </button>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>{t('settings.tabs.personal', 'Личные данные')}</span>
                                <div className={s.group}>
                                    <div className={s.floatRow}>
                                        <span className={s.rowIcon}><User size={16} strokeWidth={1.9} aria-hidden="true" /></span>
                                        <div className={s.rowField}>
                                            <input
                                                type="text"
                                                className={s.floatInput}
                                                defaultValue={nameUser}
                                                placeholder=" "
                                            />
                                            <label className={s.floatLabel}>{t('settings.personal.name', 'Имя')}</label>
                                        </div>
                                    </div>

                                    <div className={s.floatRow}>
                                        <span className={s.rowIcon}><Globe size={16} strokeWidth={1.9} aria-hidden="true" /></span>
                                        <div className={s.rowField}>
                                            <input
                                                type="text"
                                                className={s.floatInput}
                                                value={countryName}
                                                readOnly
                                                placeholder=" "
                                            />
                                            <label className={s.floatLabel}>{t('settings.personal.country', 'Страна / Регион')}</label>
                                        </div>
                                    </div>

                                    <div className={`${s.floatRow} ${s.selectWrap}`} ref={bankRef}>
                                        <span className={s.rowIcon}>
                                            {selectedBankIcon
                                                ? <BankLogo src={selectedBankIcon} alt={selectedNetwork} />
                                                : <Landmark size={16} strokeWidth={1.9} aria-hidden="true" />}
                                        </span>
                                        <button
                                            type="button"
                                            className={s.floatSelect}
                                            onClick={() => setBankOpen((v) => !v)}
                                            aria-expanded={bankOpen}
                                        >
                                            <span className={s.floatLabelUpStatic}>{t('settings.personal.bank', 'Банк')}</span>
                                            <span className={s.selectValue}>
                                                {selectedNetwork || t('settings.personal.bank', 'Банк')}
                                            </span>
                                            <ChevronDown
                                                size={15}
                                                strokeWidth={2}
                                                className={`${s.chevron} ${bankOpen ? s.chevronOpen : ''}`}
                                                aria-hidden="true"
                                            />
                                        </button>
                                        {bankOpen && (
                                            <div className={s.dropdown}>
                                                {enabledBanks.map((bankName) => {
                                                    const icon = getBankIcon(bankName, siteSettings);
                                                    return (
                                                        <button
                                                            key={bankName}
                                                            type="button"
                                                            className={`${s.option} ${bankName === selectedNetwork ? s.optionSelected : ''}`}
                                                            onClick={() => { setSelectedNetwork(bankName); setBankOpen(false); }}
                                                        >
                                                            <span className={s.optionIcon}>
                                                                {icon
                                                                    ? <BankLogo src={icon} alt="" />
                                                                    : <Landmark size={14} strokeWidth={1.9} aria-hidden="true" />}
                                                            </span>
                                                            <span className={s.optionTitle}>{bankName}</span>
                                                            {bankName === selectedNetwork && (
                                                                <Check size={15} strokeWidth={2.2} className={s.optionCheck} aria-hidden="true" />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    <div className={s.floatRow}>
                                        <span className={s.rowIcon}><Hash size={16} strokeWidth={1.9} aria-hidden="true" /></span>
                                        <div className={s.rowField}>
                                            <input
                                                type="text"
                                                className={s.floatInput}
                                                defaultValue={cryptoAdress}
                                                placeholder=" "
                                            />
                                            <label className={s.floatLabel}>{t('settings.personal.account', 'CBU/CVU')}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={s.cardActions}>
                                <button type="button" className={s.primaryBtn}>
                                    {t('settings.saveChanges', 'Сохранить изменения')}
                                </button>
                            </div>
                        </div>
                    )}

                    {currentTab === 'preferences' && (
                        <div className={s.card}>
                            <div className={s.cardHead}>
                                <h2 className={s.cardTitle}>{t('settings.preferences.title', 'Настройки')}</h2>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>{t('settings.preferences.orderConfirmation.title', 'Подтверждение ордеров')}</span>
                                <div className={s.group}>
                                    <p className={s.groupHint}>{t('settings.preferences.orderConfirmation.description', 'Jika fungsi peringatan pesanan diaktifkan, ia mesti disahkan semula setiap kali pesanan dihantar')}</p>
                                    {orderToggles.map(renderToggleRow)}
                                </div>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>{t('settings.preferences.notifications.title', 'Уведомления')}</span>
                                <div className={s.group}>
                                    <p className={s.groupHint}>{t('settings.preferences.notifications.description', 'Ini akan mempengaruhi tetapan bahasa e-mel dan pemberitahuan aplikasi')}</p>
                                    {notifToggles.map(renderToggleRow)}
                                </div>
                            </div>

                            <div className={s.cardActions}>
                                <button type="button" className={s.primaryBtn}>
                                    {t('settings.saveChanges', 'Сохранить изменения')}
                                </button>
                            </div>
                        </div>
                    )}

                    {currentTab === 'payments' && (
                        <div className={s.card}>
                            <div className={s.cardHead}>
                                <h2 className={s.cardTitle}>{t('settings.payments.title', 'Платежи')}</h2>
                                <button type="button" className={s.ghostBtn}>
                                    <Plus size={14} strokeWidth={2.1} aria-hidden="true" />
                                    {t('settings.payments.addPaymentMethod', 'Добавить способ оплаты')}
                                </button>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>{t('settings.tabs.payments', 'Платежи')}</span>
                                <div className={s.group}>
                                    <div className={s.payRow}>
                                        <span className={s.rowIcon}>
                                            {getBankIcon(network, siteSettings)
                                                ? <BankLogo src={getBankIcon(network, siteSettings)} alt={network} />
                                                : <Landmark size={16} strokeWidth={1.9} aria-hidden="true" />}
                                        </span>
                                        <div className={s.payInfo}>
                                            <span className={s.payName}>{nameUser}</span>
                                            <span className={s.payBank}>{network}</span>
                                        </div>
                                        <span className={s.payAccount}>{cryptoAdress?.substring(0, 30)}</span>
                                        <button type="button" className={s.dangerBtn}>
                                            <Trash2 size={14} strokeWidth={1.9} aria-hidden="true" />
                                            {t('settings.payments.removeButton', 'Удалить')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {currentTab === 'security' && (
                        <div className={s.card}>
                            <div className={s.cardHead}>
                                <h2 className={s.cardTitle}>{t('settings.security.title', 'Двухфакторная аутентификация (2FA)')}</h2>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>2FA</span>
                                <div className={s.group}>
                                    {securityRows2FA.map(renderSecurityRow)}
                                </div>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>{t('settings.security.advancedSecurity', 'Расширенная защита')}</span>
                                <div className={s.group}>
                                    {securityRowsAdvanced.map(renderSecurityRow)}
                                </div>
                            </div>
                        </div>
                    )}

                    {currentTab === 'address' && (
                        <div className={s.card}>
                            <div className={s.cardHead}>
                                <h2 className={s.cardTitle}>{t('settings.address.title', 'Настройки адреса')}</h2>
                                {!isAddressGenerated ? (
                                    <button
                                        type="button"
                                        className={s.primaryBtn}
                                        onClick={generateAddress}
                                        disabled={loading}
                                    >
                                        {loading && <span className={s.spinner} aria-hidden="true" />}
                                        {loading
                                            ? t('settings.address.generating', 'Генерация...')
                                            : t('settings.address.generateAddress', 'Сгенерировать адрес')}
                                    </button>
                                ) : isMatrixAnimating ? (
                                    <button type="button" className={s.primaryBtn} disabled>
                                        <span className={s.spinner} aria-hidden="true" />
                                        {t('settings.address.generating', 'Генерация...')}
                                    </button>
                                ) : (
                                    <div className={`${s.headActions} ${s.actionsIn}`}>
                                        <button type="button" className={s.primaryBtn}>
                                            {t('settings.saveChanges', 'Сохранить изменения')}
                                        </button>
                                        <button type="button" className={s.dangerBtn} onClick={resetAddress}>
                                            <Trash2 size={14} strokeWidth={1.9} aria-hidden="true" />
                                            {t('settings.address.deleteButton', 'Удалить')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className={s.addressGrid}>
                                <div className={s.qrPane}>
                                    <div className={`${s.qrBox} ${isAddressGenerated && !isMatrixAnimating ? '' : s.qrBlurred}`}>
                                        {isAddressGenerated && !isMatrixAnimating ? (
                                            <div className={`${s.qrInner} ${s.qrIn}`}>
                                                <QRCodeCanvas
                                                    value={addressF}
                                                    size={132}
                                                    bgColor={mode === 'dark' ? '#F2F4F9' : '#FFFFFF'}
                                                    fgColor="#131721"
                                                />
                                            </div>
                                        ) : (
                                            <QrCode size={72} strokeWidth={1} aria-hidden="true" />
                                        )}
                                    </div>
                                    <p className={s.qrCaption}>
                                        {!isAddressGenerated
                                            ? t('settings.address.generateQR', 'Сгенерируйте адрес, чтобы получить QR')
                                            : (!isMatrixAnimating
                                                ? t('settings.address.scanQR', 'Отсканируйте QR-код')
                                                : t('settings.address.generatingAddress', 'Генерация адреса...'))}
                                    </p>
                                </div>

                                <div className={s.addressPane}>
                                    <div className={s.section}>
                                        <span className={s.sectionTab}>
                                            {t('settings.address.uniqueAddressFor', 'Уникальный адрес для')} {nameUser}
                                        </span>
                                        <div className={s.group}>
                                            {!isAddressGenerated && !isMatrixAnimating ? (
                                                <div className={s.addressPlaceholder}>
                                                    {t('settings.address.clickGenerateAddress', 'Нажмите «Сгенерировать адрес»')}
                                                </div>
                                            ) : (
                                                <div className={`${s.addressRow} ${showSuccessFlash ? s.addressFlash : ''}`}>
                                                    <code className={s.addressText}>
                                                        {isMatrixAnimating
                                                            ? matrixAddress.split('').map((char, i) => (
                                                                <span
                                                                    key={i}
                                                                    className={matrixFixedStates[i] ? s.charFixed : s.charRandom}
                                                                >
                                                                    {char}
                                                                </span>
                                                            ))
                                                            : addressF}
                                                    </code>
                                                    <button
                                                        type="button"
                                                        className={`${s.copyBtn} ${copySuccess ? s.copied : ''}`}
                                                        onClick={() => copyToClipboard(addressF)}
                                                        disabled={isMatrixAnimating}
                                                        aria-label={t('settings.address.copy', 'Копировать')}
                                                    >
                                                        {copySuccess
                                                            ? <Check size={15} strokeWidth={2.2} aria-hidden="true" />
                                                            : <Copy size={15} strokeWidth={1.9} aria-hidden="true" />}
                                                    </button>
                                                </div>
                                            )}

                                            <div className={s.infoRow}>
                                                <span className={s.infoLabel}>{tc('settings.address.secondAddressLabel', 'Второй адрес {crypto}:')}</span>
                                                <span className={s.infoValue}>{tc('settings.address.contactSupport', 'Свяжитесь с поддержкой для генерации резервного адреса {crypto}')}</span>
                                            </div>

                                            <div className={s.infoRow}>
                                                <span className={s.infoLabel}>{t('settings.address.apiRestrictions', 'API Restrictions')}</span>
                                                <span className={s.badges}>
                                                    <span className={s.badge}>{t('settings.address.read', 'Read')}</span>
                                                    <span className={s.badge}>{t('settings.address.spotMarginTrading', 'Spot & Margin Trading')}</span>
                                                    <span className={s.badge}>{t('settings.address.margin', 'Margin')}</span>
                                                    <span className={s.badge}>{t('settings.address.futures', 'Futures')}</span>
                                                    <span className={s.badge}>{t('settings.address.withdrawals', 'Withdrawals')}</span>
                                                </span>
                                            </div>

                                            <div className={s.infoRow}>
                                                <span className={s.infoLabel}>{t('settings.address.ipAccessRestrictions', 'IP Access Restrictions')}</span>
                                                <span className={s.badges}>
                                                    <span className={s.badge}>{t('settings.address.noRestrictions', 'No Restrictions')}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Правая колонка: устройства на security, иначе FAQ */}
                <aside className={s.sideCol}>
                    {currentTab === 'security' ? (
                        <div className={s.card}>
                            <div className={s.cardHead}>
                                <h2 className={s.cardTitle}>{t('settings.devices.title', 'Устройства')}</h2>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>{t('settings.devices.currentSessions', 'Текущие сессии')}</span>
                                <div className={s.group}>
                                    <div className={s.deviceRow}>
                                        <span className={s.rowIcon}><Smartphone size={16} strokeWidth={1.9} aria-hidden="true" /></span>
                                        <div className={s.deviceInfo}>
                                            <span className={s.deviceName}>iOS 18, iPhone 16 Pro Max</span>
                                            <span className={s.deviceSub}>Dubái, EÁU</span>
                                        </div>
                                        <div className={s.deviceMeta}>
                                            <span className={s.deviceIP}>2.51.203.127</span>
                                            <span className={s.deviceTime}>2 {t('settings.devices.hoursAgo', 'ч. назад')}</span>
                                        </div>
                                    </div>
                                    <div className={s.deviceRow}>
                                        <span className={s.rowIcon}><Laptop size={16} strokeWidth={1.9} aria-hidden="true" /></span>
                                        <div className={s.deviceInfo}>
                                            <span className={s.deviceName}>macOS Sequoia, Macbook Pro M3</span>
                                            <span className={s.deviceSub}>Buenos-Aires, Argentina</span>
                                        </div>
                                        <div className={s.deviceMeta}>
                                            <span className={s.deviceIP}>45.227.164.31</span>
                                            <span className={s.deviceOnline}>
                                                <span className={s.onlineDot} aria-hidden="true" />
                                                {t('settings.devices.online', 'Онлайн')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={s.section}>
                                <span className={s.sectionTab}>{t('settings.devices.activeSessions', 'Активные сессии')}</span>
                                <div className={s.group}>
                                    <div className={s.deviceRow}>
                                        <span className={s.rowIcon}><MonitorSmartphone size={16} strokeWidth={1.9} aria-hidden="true" /></span>
                                        <div className={s.deviceInfo}>
                                            <span className={s.deviceName}>Windows 11, Dell Inspiron 15</span>
                                            <span className={s.deviceSub}>Tokio, Japón</span>
                                        </div>
                                        <div className={s.deviceMeta}>
                                            <span className={s.deviceIP}>103.105.165.27</span>
                                            <span className={s.deviceTime}>21 {t('settings.devices.minutesAgo', 'мин. назад')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={s.cardActions}>
                                <button type="button" className={s.dangerBtn}>
                                    {t('settings.devices.terminateAllSessions', 'Завершить все сессии')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className={s.card}>
                            <div className={s.cardHead}>
                                <h2 className={s.cardTitle}>{t('settings.faq.title', 'Частые вопросы')}</h2>
                                <button type="button" className={s.ghostBtn}>
                                    <HelpCircle size={14} strokeWidth={1.9} aria-hidden="true" />
                                    {t('settings.faq.helpCenter', 'Центр помощи')}
                                </button>
                            </div>

                            <div className={s.faqList}>
                                {faqs.map((faq, index) => (
                                    <div key={index} className={s.faqItem}>
                                        <button
                                            type="button"
                                            className={s.faqQuestion}
                                            aria-expanded={openFaqIndex === index}
                                            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                                        >
                                            <span>{faq.q}</span>
                                            <ChevronDown
                                                size={15}
                                                strokeWidth={2}
                                                className={`${s.chevron} ${openFaqIndex === index ? s.chevronOpen : ''}`}
                                                aria-hidden="true"
                                            />
                                        </button>
                                        <div className={`${s.faqAnswer} ${openFaqIndex === index ? s.faqOpen : ''}`}>
                                            <p>{faq.a}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}
